import { Controller } from "stimulus"

// EM-2605. The sign-in drawer in the public menu navbar. Everything happens
// inside the drawer against the platform's cross-origin endpoints (EM-2604);
// the menu page is never left or reloaded, so the basket is never at risk.
//
// States: signed-out (email form), code (6-digit entry), signed-in (greeting
// and sign out). The display cookie the platform sets seeds the greeting on
// page load; the platform's session endpoint is the truth and is asked when
// the drawer opens.
export default class extends Controller {
  static targets = ["signedOut", "code", "signedIn", "email", "codeInput", "message", "name", "codeEmail", "submit"]
  static values = {
    url: String,
    restaurantId: String,
    serviceTypeId: String,
    name: String,
    signedIn: Boolean,
    preferenceLabels: Object,
  }

  connect() {
    this.email = ""
    const signedIn = this.signedInValue || this.nameValue
    this.render(signedIn ? "signedIn" : "signedOut")
    // Deferred a tick so the menu's own ready handlers, which register the
    // listener this hands the labels to, have run whatever the load order.
    if (signedIn) window.setTimeout(() => this.applyPreferences(), 0)
  }

  // The diet and allergen choices made in the platform account area, applied
  // to this menu's own filter: fetched by name, translated to the modal's
  // label ids, and handed to the modal's script, which selects them and runs
  // one filter request. Once per page load and once after a sign-in, never on
  // a drawer open, so a filter the patron cleared stays cleared.
  applyPreferences() {
    if (!this.urlValue) return
    this.call("GET", "/patrons/preferences")
      .then(({ status, body }) => {
        if (status !== 200 || !body || !body.signed_in) return
        const labels = this.preferenceLabelsValue || {}
        const ids = []
        ;(body.diets || []).forEach((name) => { const id = (labels.diets || {})[String(name).toLowerCase()]; if (id) ids.push(id) })
        ;(body.allergens || []).forEach((name) => { const id = (labels.allergens || {})[String(name).toLowerCase()]; if (id) ids.push(id) })
        if (ids.length) document.dispatchEvent(new CustomEvent("patron-preferences:apply", { detail: { labels: ids } }))
      })
      .catch(() => {})
  }

  // The nav button dispatches this when the drawer opens.
  refresh() {
    if (!this.urlValue) return
    this.call("GET", "/patrons/session")
      .then(({ body }) => {
        if (body && body.signed_in) {
          this.nameValue = body.name || ""
          this.render("signedIn")
        } else if (this.state === "signedIn") {
          this.nameValue = ""
          this.render("signedOut")
        }
      })
      .catch(() => {})
  }

  requestCode(event) {
    event.preventDefault()
    const email = (this.hasEmailTarget ? this.emailTarget.value : this.email).trim()
    if (!email) { return this.say("Please enter your email address.", "danger") }
    this.email = email
    this.busy(true)
    this.call("POST", "/patrons/sign_in_code", {
      email, source: "menu", restaurant_id: this.restaurantIdValue, service_type_id: this.serviceTypeIdValue,
    })
      .then(({ status, body }) => {
        if (status === 200) {
          this.render("code")
          this.say(body.notice || `We've emailed a 6-digit code to ${email}. It expires in 10 minutes.`, body.notice ? "info" : "light")
        } else {
          this.say((body && body.message) || "Something went wrong - please try again.", "danger")
        }
      })
      .catch(() => this.say("We couldn't reach the sign-in service - please try again.", "danger"))
      .finally(() => this.busy(false))
  }

  resend(event) {
    event.preventDefault()
    this.requestCode(event)
  }

  verifyCode(event) {
    event.preventDefault()
    const code = this.codeInputTarget.value.trim()
    if (!code) { return this.say("Please enter the code from your email.", "danger") }
    this.busy(true)
    this.call("POST", "/patrons/sign_in_code/verify", { email: this.email, code })
      .then(({ status, body }) => {
        if (status === 200 && body.status === "ok") {
          this.nameValue = body.name || ""
          this.codeInputTarget.value = ""
          this.render("signedIn")
          this.applyPreferences()
        } else {
          this.say((body && body.message) || "That code isn't right.", "danger")
        }
      })
      .catch(() => this.say("We couldn't reach the sign-in service - please try again.", "danger"))
      .finally(() => this.busy(false))
  }

  usePassword(event) {
    event.preventDefault()
    this.render("signedOut")
  }

  signOut(event) {
    event.preventDefault()
    this.busy(true)
    this.call("DELETE", "/patrons/session")
      .then(() => {
        this.nameValue = ""
        this.email = ""
        this.render("signedOut")
      })
      .catch(() => this.say("We couldn't reach the sign-in service - please try again.", "danger"))
      .finally(() => this.busy(false))
  }

  // ---- helpers

  call(method, path, payload) {
    const options = {
      method,
      credentials: "include",
      headers: { "Accept": "application/json" },
    }
    if (payload) {
      options.headers["Content-Type"] = "application/json"
      options.body = JSON.stringify(payload)
    }
    return fetch(`${this.urlValue}${path}`, options).then((response) =>
      response.json().catch(() => ({})).then((body) => ({ status: response.status, body }))
    )
  }

  render(state) {
    this.state = state
    this.signedOutTarget.classList.toggle("d-none", state !== "signedOut")
    this.codeTarget.classList.toggle("d-none", state !== "code")
    this.signedInTarget.classList.toggle("d-none", state !== "signedIn")
    if (this.hasCodeEmailTarget) this.codeEmailTarget.textContent = this.email
    this.say("", "light")
    this.nameTargets.forEach((el) => { el.textContent = this.greeting() })
    // EM-2606: a patron the drawer just created has no name yet, and must
    // still read as signed in.
    document.querySelectorAll("[data-patron-nav-name]").forEach((el) => {
      el.textContent = state === "signedIn" ? (this.nameValue ? `Hi ${this.nameValue}` : "Signed in") : "Sign in"
    })
    if (state === "code" && this.hasCodeInputTarget) this.codeInputTarget.focus()
  }

  greeting() {
    return this.nameValue ? `Hi ${this.nameValue}` : "Hi there"
  }

  say(text, tone) {
    this.messageTarget.textContent = text
    this.messageTarget.className = `small ${text ? `alert alert-${tone} py-2 mb-2` : "d-none"}`
  }

  busy(on) {
    this.submitTargets.forEach((el) => { el.disabled = on })
  }
}
